/**
 * ONE Globe Alliance - Landing Page Component
 * Images in frontend/public/images/:
 * - Logo from URL (see constants/brand.js), footer-logo.png, cargo-ship.png
 * - event-networking.png, event-meeting.png
 * - hero-bg.png, vector-bg.png
 */

import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import Navbar from './Navbar'
import Footer from './Footer'
import './HomePage.css'

const LEAFLET_CSS_ID = 'onega-leaflet-css'
const LEAFLET_JS_ID = 'onega-leaflet-js'
const LEAFLET_CLUSTER_CSS_ID = 'onega-leaflet-cluster-css'
const LEAFLET_CLUSTER_JS_ID = 'onega-leaflet-cluster-js'
const LEAFLET_CSS_URL = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css'
const LEAFLET_JS_URL = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js'
const LEAFLET_CLUSTER_CSS_URL = 'https://unpkg.com/leaflet.markercluster@1.5.3/dist/MarkerCluster.css'
const LEAFLET_CLUSTER_JS_URL = 'https://unpkg.com/leaflet.markercluster@1.5.3/dist/leaflet.markercluster.js'
const WORLD_BOUNDS = [[-85, -180], [85, 180]]

const ensureStyle = (id, href) => {
  if (document.getElementById(id)) {
    return
  }
  const cssLink = document.createElement('link')
  cssLink.id = id
  cssLink.rel = 'stylesheet'
  cssLink.href = href
  document.head.appendChild(cssLink)
}

const ensureScript = (id, src, errorMessage) => {
  if (document.getElementById(id)) {
    return new Promise((resolve, reject) => {
      const existing = document.getElementById(id)
      if (existing.dataset.loaded === 'true') {
        resolve()
        return
      }
      existing.addEventListener('load', resolve, { once: true })
      existing.addEventListener('error', () => reject(new Error(errorMessage)), { once: true })
    })
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement('script')
    script.id = id
    script.src = src
    script.async = true
    script.onload = () => {
      script.dataset.loaded = 'true'
      resolve()
    }
    script.onerror = () => reject(new Error(errorMessage))
    document.body.appendChild(script)
  })
}

const loadLeafletAssets = async () => {
  ensureStyle(LEAFLET_CSS_ID, LEAFLET_CSS_URL)
  ensureStyle(LEAFLET_CLUSTER_CSS_ID, LEAFLET_CLUSTER_CSS_URL)

  if (!window.L) {
    await ensureScript(LEAFLET_JS_ID, LEAFLET_JS_URL, 'Failed to load map library')
  }
  if (!window.L?.markerClusterGroup) {
    await ensureScript(LEAFLET_CLUSTER_JS_ID, LEAFLET_CLUSTER_JS_URL, 'Failed to load map clustering')
  }

  return window.L
}

const toMemberCount = (value) => {
  const numeric = Number(value)
  if (!Number.isFinite(numeric)) {
    return 1
  }
  return Math.max(1, Math.floor(numeric))
}

const whyChooseCards = [
  { title: 'Strictly Vetted And Verified Members', copy: 'Only reputable, financially stable, and quality-driven companies join our network—ensuring trust in every transaction.' },
  { title: 'Strong Financial Compliance', copy: 'Our structured compliance safeguards members from payment risks and strengthens confidence across all partnerships.' },
  { title: 'Mergers And Acquisition Opportunities', copy: 'Monitor member activity, strengths, and geographic presence to identify and suggest potential pairings directly to interested members.' },
  { title: 'Digital Dashboard', copy: 'Engagement Dashboard to help members track activity, identify opportunities and measure overall engagement within the community.' },
  { title: 'Opportunity To Participate In Global And Regional Events', copy: 'We host international and regional conferences where all members meet for: One-to-one business meetings, Trade-lane development, Partnership building, Workshops and training.' },
  { title: 'Co-Branded Industry Representation', copy: 'Enhance the visibility and credibility of the members through Joint Participation in Industry Events, Co-Branded Marketing Materials, Joint PR and Media Outreach, Co-Branded Digital Visibility.' },
  { title: 'Business Development Support', copy: 'Dedicated growth initiatives, referrals, and curated networking programs that help members expand globally.' },
]

const Section = ({ children, className = '', id, style }) => (
  <section className={`home-section ${className}`} id={id} style={style}>{children}</section>
)

const Hero = () => (
  <div
    className="home-hero"
    style={{
      background: `linear-gradient(180deg, rgba(15, 23, 42, 0.4), rgba(15, 23, 42, 0.6)), url(${process.env.PUBLIC_URL || ''}/images/hero-bg.png) center/cover no-repeat`,
    }}
  >
    <div className="home-container home-hero-content" id="home">
      <h1 className="home-hero-title">
        <span className="home-text-blue">O</span><span className="home-text-purple">N</span><span className="home-text-orange">E</span> Globe.
        <br />
        <span className="home-text-blue">O</span><span className="home-text-purple">N</span><span className="home-text-orange">E</span> Alliance.
        <br />
        <span className="home-text-gradient">Infinite Possibilities.</span>
      </h1>
      <p className="home-hero-sub">
        Join a community of verified logistics partners, access smarter digital tools, and unlock faster, more reliable
        ways to quote, coordinate, and conduct business worldwide.
      </p>
      <div className="home-hero-cta">
        <Link to="/register" className="home-btn home-btn-primary">Register</Link>
        <Link to="/login" className="home-btn home-btn-glass">Member Login</Link>
      </div>
    </div>
  </div>
)

const Stats = () => (
  <Section className="home-about" id="about">
    <div className="home-container">
      <h2 className="home-about-title">About <span className="home-text-blue">O</span><span className="home-text-purple">n</span><span className="home-text-orange">e</span> Globe Alliance</h2>
      <p className="home-about-subtitle">A Unified Global Community for Independent Freight Forwarders</p>
      <p className="home-about-description">
        ONE Globe Alliance was created to empower logistics companies with a neutral, transparent, and digitally
        enabled environment that simplifies communication, enhances credibility, and drives business growth across
        borders.
      </p>
    </div>
  </Section>
)

const Services = () => (
  <Section
    className="home-services"
    id="benefits"
    style={{
      background: `linear-gradient(135deg, rgba(30, 58, 95, 0.95) 0%, rgba(45, 74, 111, 0.95) 100%), url(${process.env.PUBLIC_URL || ''}/images/vector-bg.png) center/cover no-repeat`,
    }}
  >
    <div className="home-container">
      <h2 className="home-services-title">Why Leading Forwarders Choose <span className="home-text-blue">O</span><span className="home-text-purple">n</span><span className="home-text-orange">e</span> Globe Alliance</h2>
      <p className="home-services-subtitle">A powerful, trust-driven alliance built for modern global logistics.</p>
      <div className="home-why-choose-grid">
        {whyChooseCards.map((card) => (
          <div key={card.title} className="home-why-choose-card">
            <h3>{card.title}</h3>
            <p>{card.copy}</p>
          </div>
        ))}
      </div>
    </div>
  </Section>
)

const JoinBanner = () => (
  <Section className="home-membership" id="membership">
    <div className="home-container">
      <h2 className="home-membership-title">Membership Tiers</h2>
      <p className="home-membership-description">
        Our membership tiers are designed to provide value at every level, from emerging companies to market leaders.
        Each tier unlocks premium features, enhanced visibility, and strategic advantages tailored to your business needs.
      </p>
      <div className="home-membership-grid">
        <div className="home-membership-tier">
          <h3>Founder Tier</h3>
          <p className="home-tier-label">$7,500</p>
          <p className="home-tier-description">Market Dominators - Thought leaders driving Alliance growth</p>
        </div>
        <div className="home-membership-tier">
          <h3>Titanium Tier</h3>
          <p className="home-tier-label">$5,499</p>
          <p className="home-tier-description">Market Dominators - Organic growth leaders</p>
        </div>
        <div className="home-membership-tier">
          <h3>Rhodium Tier</h3>
          <p className="home-tier-label">$4,499</p>
          <p className="home-tier-description">Segment Leading Companies</p>
        </div>
        <div className="home-membership-tier">
          <h3>Platinum Tier</h3>
          <p className="home-tier-label">$3,499</p>
          <p className="home-tier-description">Growth Companies - Most Popular</p>
        </div>
        <div className="home-membership-tier">
          <h3>Gold Tier</h3>
          <p className="home-tier-label">$2,499</p>
          <p className="home-tier-description">Hierarchal Companies with dedicated Network</p>
        </div>
        <div className="home-membership-tier">
          <h3>Silver Tier</h3>
          <p className="home-tier-label">$1,499</p>
          <p className="home-tier-description">Young and mid size Companies</p>
        </div>
      </div>
      <div className="home-cta-center">
        <Link to="/register" className="home-btn home-btn-primary home-membership-cta">Register</Link>
      </div>
    </div>
  </Section>
)

const Members = () => (
  <Section className="home-freight" id="members">
    <div className="home-container home-freight-container">
      <div className="home-freight-image">
        <img src="/images/cargo-ship.png" alt="Cargo Ship" />
      </div>
      <div className="home-freight-content">
        <h2 className="home-freight-title">Core Freight Specializations (via our members)</h2>
        <ul className="home-freight-list">
          <li>Air Freight (General, Priority, Charters)</li>
          <li>Ocean Freight (FCL, LCL, Consolidations)</li>
          <li>Project Cargo & Heavy Lift</li>
          <li>Breakbulk & OOG</li>
          <li>Cross-Trade Coordination</li>
          <li>Customs Clearance & Brokerage</li>
          <li>Warehousing & 3PL Solutions</li>
          <li>Pharma, Perishables & Temperature-Controlled Logistics</li>
          <li>E-commerce Fulfilment & Last-mile Distribution</li>
        </ul>
        <Link to="/register" className="home-btn home-btn-primary home-freight-cta">Register</Link>
      </div>
    </div>
  </Section>
)

const Experts = () => (
  <Section className="home-testimonials">
    <div className="home-container">
      <h2 className="home-testimonials-title">Member Success Stories</h2>
      <div className="home-testimonials-grid">
        <div className="home-testimonial-card">
          <p className="home-testimonial-quote">
            ONE Globe Alliance has completely transformed how we quote and confirm international shipments.
          </p>
          <p className="home-testimonial-author">— Member 1</p>
        </div>
        <div className="home-testimonial-card">
          <p className="home-testimonial-quote">
            The financial protection program and verified partners give us unmatched confidence.
          </p>
          <p className="home-testimonial-author">— Member 2</p>
        </div>
      </div>
    </div>
  </Section>
)

const GlobalMap = () => {
  const mapContainerRef = useRef(null)
  const mapInstanceRef = useRef(null)
  const [mapState, setMapState] = useState({
    loading: true,
    error: '',
    locations: 0,
    members: 0,
  })

  useEffect(() => {
    let mounted = true
    let onResize = null

    const initializeMap = async () => {
      try {
        const L = await loadLeafletAssets()
        if (!mounted || !mapContainerRef.current) {
          return
        }

        const map = L.map(mapContainerRef.current, {
          center: [20, 0],
          zoom: 2,
          minZoom: 2,
          maxZoom: 5,
          scrollWheelZoom: false,
          worldCopyJump: false,
          zoomControl: true,
          maxBounds: WORLD_BOUNDS,
          maxBoundsViscosity: 1.0,
          inertia: false,
        })
        mapInstanceRef.current = map

        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: '&copy; OpenStreetMap contributors',
          noWrap: true,
          bounds: WORLD_BOUNDS,
        }).addTo(map)

        const response = await fetch(`${process.env.PUBLIC_URL || ''}/data/member-locations.json`, { cache: 'no-store' })
        if (!response.ok) {
          throw new Error('Unable to fetch member location data')
        }

        const locations = await response.json()
        if (!Array.isArray(locations)) {
          throw new Error('Invalid member location data')
        }

        let totalMembers = 0
        let totalLocations = 0
        const clusterGroup = L.markerClusterGroup({
          showCoverageOnHover: false,
          zoomToBoundsOnClick: true,
          spiderfyOnMaxZoom: true,
          maxClusterRadius: 52,
          iconCreateFunction: (cluster) => {
            const membersInCluster = cluster
              .getAllChildMarkers()
              .reduce((sum, marker) => sum + (marker.options.memberCount || 1), 0)
            const displayCount = membersInCluster > 99 ? '99+' : `${membersInCluster}`
            const size = membersInCluster >= 25 ? 46 : membersInCluster >= 10 ? 40 : 34

            return L.divIcon({
              html: `<div class="home-map-cluster-badge"><span>${displayCount}</span></div>`,
              className: 'home-map-cluster-icon',
              iconSize: L.point(size, size),
            })
          },
        })

        locations.forEach((location) => {
          if (!Array.isArray(location.position) || location.position.length !== 2) {
            return
          }

          const latitude = Number(location.position[0])
          const longitude = Number(location.position[1])
          if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
            return
          }

          const memberCount = toMemberCount(location.members)
          totalMembers += memberCount
          totalLocations += 1

          const markerSize = Math.min(34, Math.max(18, 16 + Math.floor(memberCount / 2)))

          const marker = L.marker([latitude, longitude], {
            memberCount,
            icon: L.divIcon({
              html: `<div class="home-map-point-badge"><span>${memberCount}</span></div>`,
              className: 'home-map-point-icon',
              iconSize: [markerSize, markerSize],
              iconAnchor: [markerSize / 2, markerSize / 2],
              popupAnchor: [0, -markerSize / 2],
            }),
          })

          marker.bindPopup(
            `<div class="home-map-popup"><strong>${location.name || 'Member Hub'}</strong><br/>${memberCount} member${memberCount === 1 ? '' : 's'}</div>`
          )
          marker.bindTooltip(`${location.name || 'Member Hub'} (${memberCount})`, {
            direction: 'top',
            offset: [0, -6],
          })
          clusterGroup.addLayer(marker)
        })

        map.addLayer(clusterGroup)
        map.setView([20, 0], 2)
        map.panInsideBounds(WORLD_BOUNDS, { animate: false })

        map.invalidateSize()
        onResize = () => map.invalidateSize()
        window.addEventListener('resize', onResize)

        if (mounted) {
          setMapState({
            loading: false,
            error: '',
            locations: totalLocations,
            members: totalMembers,
          })
        }
      } catch (error) {
        if (mounted) {
          setMapState({
            loading: false,
            error: 'Unable to load map right now. Please try again shortly.',
            locations: 0,
            members: 0,
          })
        }
      }
    }

    initializeMap()

    return () => {
      mounted = false
      if (onResize) {
        window.removeEventListener('resize', onResize)
      }
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove()
        mapInstanceRef.current = null
      }
    }
  }, [])

  return (
    <Section className="home-global-map">
      <div className="home-container">
        <h2 className="home-map-title">Global Member Map</h2>
        <p className="home-map-subtitle">
          An interactive world map highlighting our presence.<br />
          Click on markers to see member details.
        </p>
        <div className="home-map-shell">
          <div
            className="home-map-canvas"
            ref={mapContainerRef}
            aria-label="Interactive global member map"
            role="region"
          />
          {mapState.loading && <div className="home-map-overlay">Loading member locations...</div>}
          {mapState.error && <div className="home-map-overlay home-map-overlay-error">{mapState.error}</div>}
        </div>
        <p className="home-map-meta">
          {mapState.locations > 0
            ? `Showing ${mapState.locations} member hubs with ${mapState.members} active member connections.`
            : 'Global network coverage across 200+ countries'}
        </p>
      </div>
    </Section>
  )
}

const Events = () => (
  <Section className="home-events" id="events">
    <div className="home-container">
      <h2 className="home-events-title">Upcoming Events & Conferences</h2>
      <p className="home-events-subtitle">Connect. Grow. Partner.</p>
      <div className="home-events-grid">
        <div className="home-event-card">
          <div className="home-event-image-wrapper">
            <img src="/images/event-networking.png" alt="Networking Event" className="home-event-img" />
            <div className="home-event-overlay">Networking Event 1</div>
          </div>
          <div className="home-event-details">
            <h3>ONE Globe Alliance Global Conference 2026</h3>
            <p className="home-event-location">Details TBA</p>
          </div>
        </div>
        <div className="home-event-card">
          <div className="home-event-image-wrapper">
            <img src="/images/event-meeting.png" alt="Business Meeting" className="home-event-img" />
            <div className="home-event-overlay">Business Meeting</div>
          </div>
          <div className="home-event-details">
            <h3>Regional Networking Meet</h3>
            <p className="home-event-location">Various Locations</p>
          </div>
        </div>
      </div>
      <div className="home-events-list">
        <ul>
          <li>Regional Networking Events across Asia, Middle East, Americas & Europe</li>
          <li>Virtual Member Meetups (Quarterly)</li>
        </ul>
      </div>
    </div>
  </Section>
)

const Partners = () => (
  <Section className="home-cta-section">
    <div className="home-container">
      <h2 className="home-cta-title">
        Become Part of <span className="home-text-blue">O</span><span className="home-text-purple">n</span><span className="home-text-orange">e</span> Global<br />Alliance That Drives Possibilities
      </h2>
      <p className="home-cta-description">
        Join a network built on trust, technology, and growth-focused collaboration.
      </p>
      <div className="home-cta-center">
        <Link to="/register" className="home-btn home-btn-primary home-cta-button">Register</Link>
      </div>
    </div>
  </Section>
)

const HomePage = () => (
  <div className="home-page">
    <Navbar />
    <Hero />
    <Stats />
    <Services />
    <JoinBanner />
    <Members />
    <Experts />
    <GlobalMap />
    <Events />
    <Partners />
    <Footer />
  </div>
)

export default HomePage
